import styled from "styled-components";

/**
 * YogGoalAsanas.styles.js
 *
 * v2 — rebuilt around a filterable practice-library layout (breadcrumb →
 * hero card → category pills → sidebar filters + expandable video list →
 * pagination → bottom CTA), replacing the earlier hero + single-player +
 * card-grid layout. Kept the same sage/gold palette used throughout this
 * module (#3d6b4f sage, #c9a84c gold, #2e1f0f ink) for continuity rather
 * than introducing a fourth color scheme into the app.
 *
 * This is a from-scratch rewrite of this file — if anything elsewhere in
 * the app imports individual exports from here (e.g. the old `GoalPill`),
 * double check before dropping this in, since names/shapes changed.
 */

export const Page = styled.main`
  min-height: 100vh;

  background: #ffffff;

  color: #2e1f0f;

  padding: 28px 24px 90px;

  @media (min-width: 900px) {
    padding: 32px 40px 110px;
  }
`;

export const PageInner = styled.div`
  max-width: 1180px;

  margin: 0 auto;
`;

/* ── Breadcrumb ─────────────────────────────────────────────────────── */

export const Breadcrumb = styled.nav`
  display: flex;

  align-items: center;

  flex-wrap: wrap;

  gap: 6px;

  margin-bottom: 22px;

  font-size: 0.82rem;

  color: rgba(46, 31, 15, 0.5);
`;

export const BreadcrumbLink = styled.a`
  color: inherit;

  text-decoration: none;

  &:hover {
    color: #3d6b4f;

    text-decoration: underline;
  }
`;

export const BreadcrumbCurrent = styled.span`
  color: #2e1f0f;

  font-weight: 700;
`;

export const BreadcrumbSep = styled.span`
  color: rgba(46, 31, 15, 0.28);
`;

/* ── Hero card ──────────────────────────────────────────────────────── */

export const HeroCard = styled.section`
  display: grid;

  grid-template-columns: 1fr;

  gap: 28px;

  align-items: center;

  padding: 32px 28px;

  margin-bottom: 26px;

  border-radius: 24px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(61, 107, 79, 0.12),
      transparent 55%
    ),
    #faf6ee;

  @media (min-width: 860px) {
    grid-template-columns: 1.15fr 0.85fr;

    padding: 40px 44px;
  }
`;

export const HeroTitle = styled.h1`
  margin: 0;

  font-size: clamp(2rem, 4.6vw, 3rem);

  line-height: 1.05;

  letter-spacing: -0.03em;

  font-weight: 800;

  color: #2c5940;
`;

export const HeroDivider = styled.div`
  display: flex;

  align-items: center;

  gap: 8px;

  margin: 14px 0 16px;

  color: rgba(61, 107, 79, 0.55);

  &::before,
  &::after {
    content: "";

    width: 22px;

    height: 1px;

    background: currentColor;
  }
`;

export const HeroDescription = styled.p`
  max-width: 480px;

  margin: 0 0 20px;

  color: rgba(46, 31, 15, 0.7);

  font-size: 1rem;

  line-height: 1.65;
`;

export const HeroStatsRow = styled.div`
  display: flex;

  flex-wrap: wrap;

  gap: 20px;

  margin-bottom: 12px;
`;

export const HeroStat = styled.div`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  font-size: 0.88rem;

  font-weight: 600;

  color: #3d3226;

  svg {
    flex: 0 0 auto;

    color: #3d6b4f;
  }
`;

export const HeroFocusRow = styled.div`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  font-size: 0.85rem;

  color: rgba(46, 31, 15, 0.65);

  svg {
    flex: 0 0 auto;

    color: #c9a84c;
  }
`;

/* Decorative visual — an abstract lotus/glow panel rather than a stock
   photo, so this doesn't depend on a real image asset shipping with it.
   Drop a real photo in via a background-image override on this
   component (or swap it for an <img>) once one's available. */
export const HeroVisual = styled.div`
  position: relative;

  display: none;

  border-radius: 18px;

  overflow: hidden;

  aspect-ratio: 4 / 3;

  background:
    radial-gradient(
      circle at 30% 20%,
      rgba(255, 255, 255, 0.9),
      transparent 55%
    ),
    linear-gradient(160deg, #eef3ea, #f7ecd9 70%);

  @media (min-width: 860px) {
    display: block;
  }
`;

export const HeroVisualGlyph = styled.svg`
  position: absolute;

  inset: 0;

  width: 100%;

  height: 100%;
`;

/* ── Category pills ────────────────────────────────────────────────── */

export const CategoryPillsRow = styled.div`
  display: flex;

  flex-wrap: wrap;

  gap: 10px;

  margin-bottom: 26px;
`;

export const CategoryPill = styled.button`
  padding: 9px 18px;

  border-radius: 999px;

  border: 1px solid
    ${({ $active }) => ($active ? "#2c5940" : "rgba(46,31,15,0.14)")};

  background: ${({ $active }) => ($active ? "#2c5940" : "#ffffff")};

  color: ${({ $active }) => ($active ? "#fff" : "#3d3226")};

  font-size: 0.82rem;

  font-weight: 700;

  cursor: pointer;

  transition:
    background 150ms ease,
    color 150ms ease,
    border-color 150ms ease;

  &:hover {
    border-color: #2c5940;
  }

  &:focus-visible {
    outline: 3px solid rgba(61, 107, 79, 0.35);

    outline-offset: 2px;
  }
`;

/* ── Layout: sidebar + main ────────────────────────────────────────── */

export const LayoutGrid = styled.div`
  display: grid;

  grid-template-columns: 1fr;

  gap: 26px;

  align-items: start;

  @media (min-width: 980px) {
    grid-template-columns: 250px 1fr;
  }
`;

export const Sidebar = styled.aside`
  padding: 20px;

  border: 1px solid rgba(46, 31, 15, 0.08);

  border-radius: 16px;

  background: #fcfbf8;

  position: sticky;

  top: 20px;
`;

export const SidebarHeader = styled.div`
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 14px;

  font-weight: 800;

  font-size: 1rem;
`;

export const ClearAllButton = styled.button`
  border: none;

  background: none;

  padding: 0;

  color: #3d6b4f;

  font-size: 0.78rem;

  font-weight: 700;

  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const FilterGroup = styled.fieldset`
  border: none;

  padding: 0;

  margin: 0 0 18px;

  max-height: ${({ $scroll }) => ($scroll ? "180px" : "none")};

  overflow-y: ${({ $scroll }) => ($scroll ? "auto" : "visible")};
`;

export const FilterGroupTitle = styled.legend`
  padding: 0;

  margin-bottom: 10px;

  font-size: 0.78rem;

  font-weight: 800;

  letter-spacing: 0.03em;

  text-transform: uppercase;

  color: #3d3226;
`;

export const FilterOption = styled.label`
  display: flex;

  align-items: center;

  gap: 9px;

  padding: 5px 0;

  font-size: 0.87rem;

  color: #3d3226;

  cursor: pointer;

  input {
    width: 15px;

    height: 15px;

    accent-color: #3d6b4f;

    cursor: pointer;
  }
`;

export const ApplyFiltersButton = styled.button`
  width: 100%;

  padding: 11px;

  margin-top: 4px;

  border: none;

  border-radius: 10px;

  background: #2c5940;

  color: #fff;

  font-size: 0.86rem;

  font-weight: 700;

  cursor: pointer;

  transition: background 150ms ease;

  &:hover {
    background: #234a34;
  }

  &:focus-visible {
    outline: 3px solid rgba(61, 107, 79, 0.4);

    outline-offset: 2px;
  }
`;

export const MainCol = styled.div`
  min-width: 0;
`;

export const ResultsBar = styled.div`
  display: flex;

  align-items: center;

  justify-content: space-between;

  flex-wrap: wrap;

  gap: 10px;

  margin-bottom: 16px;
`;

export const ResultsCount = styled.div`
  font-size: 0.9rem;

  color: rgba(46, 31, 15, 0.6);
`;

export const SortSelect = styled.select`
  padding: 8px 12px;

  border-radius: 9px;

  border: 1px solid rgba(46, 31, 15, 0.16);

  background: #fff;

  font-size: 0.85rem;

  color: #3d3226;

  cursor: pointer;

  &:focus-visible {
    outline: 3px solid rgba(61, 107, 79, 0.3);

    outline-offset: 1px;
  }
`;

/* ── Video list cards ──────────────────────────────────────────────── */

export const VideoList = styled.div`
  display: flex;

  flex-direction: column;

  gap: 16px;

  margin-bottom: 26px;
`;

export const VideoListCard = styled.article`
  border: 1px solid
    ${({ $expanded }) =>
      $expanded ? "rgba(61,107,79,0.35)" : "rgba(46,31,15,0.09)"};

  border-radius: 16px;

  background: #fff;

  overflow: hidden;

  transition: border-color 150ms ease;
`;

export const VideoListCardHeader = styled.button`
  display: grid;

  grid-template-columns: 128px 1fr;

  gap: 16px;

  width: 100%;

  padding: 14px;

  border: none;

  background: none;

  text-align: left;

  cursor: pointer;

  @media (max-width: 560px) {
    grid-template-columns: 96px 1fr;
  }
`;

export const VideoThumb = styled.div`
  position: relative;

  border-radius: 10px;

  overflow: hidden;

  aspect-ratio: 16 / 10;

  background: #eee8df;

  img {
    width: 100%;

    height: 100%;

    object-fit: cover;

    display: block;
  }
`;

export const VideoThumbPlay = styled.span`
  position: absolute;

  inset: 0;

  display: grid;

  place-items: center;

  background: rgba(0, 0, 0, 0.16);

  color: #fff;

  font-size: 0.9rem;
`;

export const VideoThumbDuration = styled.span`
  position: absolute;

  right: 6px;

  bottom: 6px;

  padding: 3px 6px;

  border-radius: 5px;

  background: rgba(0, 0, 0, 0.78);

  color: #fff;

  font-size: 0.62rem;

  font-weight: 800;
`;

export const VideoInfo = styled.div`
  min-width: 0;
`;

export const VideoTitleRow = styled.div`
  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 10px;
`;

export const VideoTitle = styled.h3`
  margin: 0;

  font-size: 1.05rem;

  line-height: 1.3;

  color: #22331f;
`;

export const VideoActions = styled.div`
  display: flex;

  align-items: center;

  gap: 6px;

  flex: 0 0 auto;
`;

export const IconButton = styled.span`
  display: inline-flex;

  align-items: center;

  justify-content: center;

  width: 28px;

  height: 28px;

  border-radius: 8px;

  color: ${({ $active }) => ($active ? "#2c5940" : "#8a7a63")};

  background: ${({ $active }) =>
    $active ? "rgba(61,107,79,0.12)" : "transparent"};

  transition:
    background 150ms ease,
    color 150ms ease;
`;

export const CategoryTag = styled.span`
  display: inline-block;

  margin: 6px 0 8px;

  padding: 3px 9px;

  border-radius: 999px;

  background: rgba(61, 107, 79, 0.1);

  color: #2c5940;

  font-size: 0.68rem;

  font-weight: 800;
`;

export const VideoDescription = styled.p`
  margin: 0 0 8px;

  color: rgba(46, 31, 15, 0.62);

  font-size: 0.86rem;

  line-height: 1.55;

  display: -webkit-box;

  -webkit-line-clamp: 2;

  -webkit-box-orient: vertical;

  overflow: hidden;
`;

export const BenefitTagsRow = styled.div`
  display: flex;

  flex-wrap: wrap;

  gap: 5px 14px;

  font-size: 0.76rem;

  color: rgba(46, 31, 15, 0.55);
`;

export const BenefitTag = styled.span`
  display: inline-flex;

  align-items: center;

  gap: 5px;

  &::before {
    content: "";

    width: 4px;

    height: 4px;

    border-radius: 50%;

    background: #3d6b4f;
  }
`;

/* ── Expanded card panel ───────────────────────────────────────────── */

export const VideoExpandedPanel = styled.div`
  padding: 0 14px 18px;

  border-top: 1px solid rgba(46, 31, 15, 0.08);
`;

export const InlinePlayerWrap = styled.div`
  position: relative;

  aspect-ratio: 16 / 9;

  margin: 14px 0;

  border-radius: 12px;

  overflow: hidden;

  background: #111;

  iframe {
    position: absolute;

    inset: 0;

    width: 100%;

    height: 100%;

    border: 0;
  }
`;

export const Tabs = styled.div`
  display: flex;

  gap: 22px;

  border-bottom: 1px solid rgba(46, 31, 15, 0.1);

  margin-bottom: 14px;
`;

export const TabButton = styled.button`
  padding: 8px 2px 10px;

  border: none;

  border-bottom: 2px solid
    ${({ $active }) => ($active ? "#2c5940" : "transparent")};

  background: none;

  color: ${({ $active }) => ($active ? "#2c5940" : "rgba(46,31,15,0.5)")};

  font-size: 0.86rem;

  font-weight: 700;

  cursor: pointer;

  &:hover {
    color: #2c5940;
  }
`;

export const TabPanel = styled.div`
  color: rgba(46, 31, 15, 0.72);

  font-size: 0.9rem;

  line-height: 1.65;
`;

export const BenefitsList = styled.ul`
  margin: 0;

  padding: 0 0 0 18px;

  li {
    margin-bottom: 6px;
  }
`;

export const ExpandedActions = styled.div`
  display: flex;

  flex-wrap: wrap;

  gap: 10px;

  margin-top: 18px;
`;

export const WatchedButton = styled.button`
  display: inline-flex;

  align-items: center;

  gap: 7px;

  padding: 10px 16px;

  border: none;

  border-radius: 10px;

  background: ${({ $done }) => ($done ? "#234a34" : "#2c5940")};

  color: #fff;

  font-size: 0.85rem;

  font-weight: 700;

  cursor: pointer;

  transition: background 150ms ease;

  &:hover {
    background: #234a34;
  }
`;

/* ── Pagination ─────────────────────────────────────────────────────── */

export const PaginationRow = styled.div`
  display: flex;

  align-items: center;

  justify-content: center;

  gap: 8px;

  margin-bottom: 34px;
`;

export const PageNavButton = styled.button`
  display: inline-flex;

  align-items: center;

  justify-content: center;

  width: 32px;

  height: 32px;

  border-radius: 50%;

  border: 1px solid rgba(46, 31, 15, 0.14);

  background: #fff;

  color: #3d3226;

  cursor: pointer;

  &:disabled {
    opacity: 0.35;

    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: #2c5940;
  }
`;

export const PageButton = styled.button`
  display: inline-flex;

  align-items: center;

  justify-content: center;

  width: 32px;

  height: 32px;

  border-radius: 50%;

  border: none;

  background: ${({ $active }) => ($active ? "#2c5940" : "transparent")};

  color: ${({ $active }) => ($active ? "#fff" : "#3d3226")};

  font-size: 0.85rem;

  font-weight: 700;

  cursor: pointer;

  &:hover {
    background: ${({ $active }) =>
      $active ? "#2c5940" : "rgba(46,31,15,0.06)"};
  }
`;

/* ── Empty state ────────────────────────────────────────────────────── */

export const EmptyState = styled.section`
  max-width: 700px;

  margin: 70px auto;

  padding: 50px 30px;

  text-align: center;

  border-radius: 26px;

  background: rgba(250, 246, 238, 0.9);
`;

export const EmptyStateTitle = styled.h1`
  margin: 0;

  font-size: clamp(1.6rem, 4vw, 2.2rem);

  color: #2c5940;
`;

export const EmptyStateText = styled.p`
  max-width: 480px;

  margin: 14px auto 0;

  color: rgba(46, 31, 15, 0.6);

  line-height: 1.7;
`;